import React, { useState } from 'react';
import Task from '../../../types/Task';
import { Epic } from '../../../types/Epic';
import {
  TaskDependencies as TaskDependenciesData,
  TaskDependencyDirection,
  TaskDependencyEdge,
  TaskDependencyTask,
} from '../../../types/TaskDependency';
import TaskDependencyDropdown from '../../../components/UI/Dropdown/TaskDependencyDropdown';
import { TaskTypeLabel } from '../../../components/UI/Dropdown/TaskTypeDropdown';
import { TaskType } from '../../../types/TaskType';
import { GoToIcon } from '../../../assets/CustomIcons/GoToIcon';
import { CloseOutlined } from '@ant-design/icons';
import CustomTooltip from '../../../components/UI/Tooltip/CustomTooltip';
import TaskDependencyTooltip from '../../../components/UI/Tooltip/TaskDependencyTooltip';

interface TaskDependenciesProps {
  task: Task;
  dependencies: TaskDependenciesData;
  /** Same-workspace, non-epic tasks the user can pick (e.g. parentTaskOptions). */
  options: Task[];
  epics: Epic[];
  onAdd: (relatedTaskId: number, type: TaskDependencyDirection) => void;
  onRemove: (dependencyId: number, relatedTaskId: number) => void;
  onOpenTask: (relatedTask: TaskDependencyTask) => void;
}

const DependencyRow = (props: {
  label: string;
  direction: TaskDependencyDirection;
  edges: TaskDependencyEdge[];
  options: Task[];
  epics: Epic[];
  excludeTaskIds: number[];
  onAdd: (relatedTaskId: number, type: TaskDependencyDirection) => void;
  onRemove: (dependencyId: number, relatedTaskId: number) => void;
  onOpenTask: (relatedTask: TaskDependencyTask) => void;
}) => {
  // The value itself is the dropdown trigger (mirrors Parent / Epic): clicking a
  // chip opens the picker to add another; the hover arrow opens the task; the
  // hover × removes it. When there are no values, match the table view's empty-cell
  // pattern: static "None" that swaps to "Choose Blockers" on hover (no tooltip). (#2614)
  const [isHoveringEmpty, setIsHoveringEmpty] = useState(false);
  return (
    <div className="min-h-[40px] flex items-center justify-start gap-10">
      <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">{props.label}</div>
      <div className="flex items-center min-w-0 flex-1 px-1 cursor-pointer">
        <TaskDependencyDropdown
          options={props.options}
          epics={props.epics}
          excludeTaskIds={props.excludeTaskIds}
          selectedEdges={props.edges}
          onAdd={(selected) => props.onAdd(selected.id, props.direction)}
          onRemove={(dependencyId, relatedTaskId) => props.onRemove(dependencyId, relatedTaskId)}
          onRemoveAll={() => props.edges.forEach((edge) => props.onRemove(edge.id, edge.task.id))}
          removeAllLabel={props.direction === 'blockedBy' ? 'Remove All Blockers' : 'Remove All Blocking'}
          trigger={
            props.edges.length === 0 ? (
              <div
                className="px-1 py-0.5 text-neutral-900 text-sm font-normal"
                onMouseEnter={() => setIsHoveringEmpty(true)}
                onMouseLeave={() => setIsHoveringEmpty(false)}
              >
                {isHoveringEmpty ? 'Choose Blockers' : 'None'}
              </div>
            ) : props.edges.length === 1 ? (
              // Single link: the value keeps its in-cell "go to" arrow and hover × remove,
              // and hovering reveals the untruncated title. (#2599)
              <CustomTooltip title={props.edges[0].task.title}>
                <div className="relative group/chip flex items-center min-w-0 max-w-full w-full pr-12 rounded hover:bg-neutral-200">
                  <div className="flex items-center gap-1 min-w-0 px-1 py-0.5">
                    <TaskTypeLabel type={props.edges[0].task.type as TaskType} size="small" />
                    <span className="truncate text-neutral-900 text-sm">{props.edges[0].task.title}</span>
                  </div>
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover/chip:flex items-center gap-1">
                    <GoToIcon
                      className="text-neutral-900 bg-neutral-300 rounded p-1 cursor-pointer"
                      onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        props.onOpenTask(props.edges[0].task);
                      }}
                    />
                    <CloseOutlined
                      className="text-neutral-600 hover:text-red-600 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        props.onRemove(props.edges[0].id, props.edges[0].task.id);
                      }}
                    />
                  </div>
                </div>
              </CustomTooltip>
            ) : (
              // Several links: the row never grows past "first + N" — hovering lists them all,
              // each offering "go to" and "remove". Mirrors the table cell + Labels row. (#2599)
              <TaskDependencyTooltip edges={props.edges} onOpenTask={props.onOpenTask} onRemove={props.onRemove}>
                <div className="flex items-center gap-1 min-w-0 max-w-full px-1 py-0.5">
                  <TaskTypeLabel type={props.edges[0].task.type as TaskType} size="small" />
                  <span className="truncate min-w-0 text-neutral-900 text-sm">{props.edges[0].task.title}</span>
                  <span className="text-neutral-700 text-xs shrink-0">+{props.edges.length - 1}</span>
                </div>
              </TaskDependencyTooltip>
            )
          }
        />
      </div>
    </div>
  );
};

export const TaskDependencies: React.FC<TaskDependenciesProps> = ({
  task,
  dependencies,
  options,
  epics,
  onAdd,
  onRemove,
  onOpenTask,
}) => {
  // Each direction hides self + tasks linked in the OPPOSITE direction (a task can't both
  // block and be blocked by this one); same-direction links stay visible, rendered checked.
  const blockingIds = dependencies.blocking.map((e) => e.task.id);
  const blockedByIds = dependencies.blockedBy.map((e) => e.task.id);

  return (
    <>
      <DependencyRow
        label="Blocking"
        direction="blocking"
        edges={dependencies.blocking}
        options={options}
        epics={epics}
        excludeTaskIds={[task.id, ...blockedByIds]}
        onAdd={onAdd}
        onRemove={onRemove}
        onOpenTask={onOpenTask}
      />
      <DependencyRow
        label="Blocked by"
        direction="blockedBy"
        edges={dependencies.blockedBy}
        options={options}
        epics={epics}
        excludeTaskIds={[task.id, ...blockingIds]}
        onAdd={onAdd}
        onRemove={onRemove}
        onOpenTask={onOpenTask}
      />
    </>
  );
};
